import type { AxiosError, AxiosInstance } from 'axios';
import { openDepositLimitGateImperative } from '@/store/depositLimitGateStore';
import type { DepositLimitExceededBody } from './types';

/**
 * Backstop for the server-side deposit-limit enforcement (CP-007b). A 403
 * `{ code: 'DEPOSIT_LIMIT_EXCEEDED', window, limitCents, remainingCents }` from
 * DepositService.createIntent surfaces the limit popup. There's no replay — a cap
 * can't be cleared in-session — so the original 403 always propagates after
 * opening the popup.
 *
 * This must be its OWN interceptor (matching on the structured `code`) because
 * `parsePaymentApiError` flattens every 403 to a generic "no permission" string,
 * which would otherwise swallow the breach. The deposit form additionally
 * suppresses its inline pill for this code via `isDepositLimitExceededError`.
 *
 * Returns a teardown function (tests / HMR).
 */
export function installDepositLimitInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    // Async so a synchronous throw surfaces as a rejected promise to the caller.
    async (error: AxiosError) => {
      const body = error.response?.data as DepositLimitExceededBody | undefined;
      if (error.response?.status === 403 && body?.code === 'DEPOSIT_LIMIT_EXCEEDED') {
        openDepositLimitGateImperative({
          window: body.window,
          limitCents: body.limitCents,
          remainingCents: body.remainingCents,
        });
      }
      throw error;
    },
  );
  return () => instance.interceptors.response.eject(id);
}
